import { openDB } from 'idb';

const DB_NAME = 'golf-offline-db';
const DB_VERSION = 4;

let dbPromise;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          db.createObjectStore('courses', { keyPath: 'id' });
          db.createObjectStore('scoringGroups', { keyPath: ['tournamentId', 'roundNumber', 'groupNumber'] });
          db.createObjectStore('scores', { keyPath: ['roundId', 'holeNumber'] });
          db.createObjectStore('leaderboards', { keyPath: ['tournamentId', 'roundNumber'] });
          const syncStore = db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
          syncStore.createIndex('status', 'status');
          syncStore.createIndex('createdAt', 'createdAt');
        }
        if (oldVersion < 2) {
          db.createObjectStore('offlineSessions', { keyPath: 'id' });
        }
        if (oldVersion < 3) {
          // Shared-ball team formats (Foursomes/Greensomes/Texas Scramble) score
          // via TeamRound, a separate id space from Round — a SEPARATE store
          // (not reusing 'scores') so a team_round id can never collide with a
          // player round id that happens to share the same number.
          db.createObjectStore('teamScores', { keyPath: ['teamRoundId', 'holeNumber'] });
        }
        if (oldVersion < 4) {
          // Personal hole stats. Same key shape as 'scores' and against the
          // same round ids, but kept apart because it is a different record
          // with a different endpoint — merging them would mean every score
          // read carrying six fields it has no use for, which is exactly the
          // reason the server keeps hole_stat off hole_score too.
          db.createObjectStore('holeStats', { keyPath: ['roundId', 'holeNumber'] });
        }
      },
    });
  }
  return dbPromise;
}

// --- Courses ---

export async function getCourse(id) {
  const db = await getDb();
  return db.get('courses', id);
}

export async function putCourse(course) {
  const db = await getDb();
  return db.put('courses', { ...course, _cachedAt: Date.now() });
}

// --- Scoring Groups ---

export async function getScoringGroups(tournamentId, roundNumber) {
  const db = await getDb();
  const all = await db.getAll('scoringGroups');
  return all.filter(
    (g) => String(g.tournamentId) === String(tournamentId) &&
      (roundNumber == null || g.roundNumber === roundNumber)
  );
}

export async function putScoringGroups(tournamentId, groups) {
  const db = await getDb();
  const tx = db.transaction('scoringGroups', 'readwrite');
  for (const group of groups) {
    await tx.store.put({
      ...group,
      tournamentId,
      roundNumber: group.round_number,
      groupNumber: group.group_number,
      _cachedAt: Date.now(),
    });
  }
  await tx.done;
}

// --- Scores ---

export async function getScores(roundIds) {
  const db = await getDb();
  const all = await db.getAll('scores');
  const idSet = new Set(roundIds.map(String));
  return all.filter((s) => idSet.has(String(s.roundId)));
}

export async function putScore(roundId, holeNumber, grossScore, source = 'server') {
  const db = await getDb();
  return db.put('scores', {
    roundId,
    holeNumber,
    grossScore,
    _source: source,
    _cachedAt: Date.now(),
  });
}

export async function putScoresBatch(scores) {
  const db = await getDb();
  const tx = db.transaction('scores', 'readwrite');
  for (const s of scores) {
    await tx.store.put({
      roundId: s.round_id,
      holeNumber: s.hole_number,
      grossScore: s.gross_score,
      _source: s._source || 'server',
      _cachedAt: Date.now(),
    });
  }
  await tx.done;
}

export async function markScoreSynced(roundId, holeNumber) {
  const db = await getDb();
  const existing = await db.get('scores', [roundId, holeNumber]);
  if (existing) {
    existing._source = 'server';
    await db.put('scores', existing);
  }
}

// --- Team Scores (Foursomes/Greensomes/Texas Scramble) ---
// Mirrors the Scores functions above exactly, against the separate
// 'teamScores' store — see the DB_VERSION 3 upgrade comment for why.

export async function getTeamScores(teamRoundIds) {
  const db = await getDb();
  const all = await db.getAll('teamScores');
  const idSet = new Set(teamRoundIds.map(String));
  return all.filter((s) => idSet.has(String(s.teamRoundId)));
}

export async function putTeamScore(teamRoundId, holeNumber, grossScore, source = 'server') {
  const db = await getDb();
  return db.put('teamScores', {
    teamRoundId,
    holeNumber,
    grossScore,
    _source: source,
    _cachedAt: Date.now(),
  });
}

export async function markTeamScoreSynced(teamRoundId, holeNumber) {
  const db = await getDb();
  const existing = await db.get('teamScores', [teamRoundId, holeNumber]);
  if (existing) {
    existing._source = 'server';
    await db.put('teamScores', existing);
  }
}

// --- Hole Stats (personal: fairway, GIR, up & down, approaches, putts) ---
// Mirrors the Scores functions above, against the 'holeStats' store — see the
// DB_VERSION 4 upgrade comment for why it is separate.

export async function getHoleStats(roundId) {
  const db = await getDb();
  const all = await db.getAll('holeStats');
  return all.filter((s) => String(s.roundId) === String(roundId));
}

export async function putHoleStat(roundId, holeNumber, stats, source = 'server') {
  const db = await getDb();
  return db.put('holeStats', {
    roundId,
    holeNumber,
    stats,
    _source: source,
    _cachedAt: Date.now(),
  });
}

export async function markHoleStatSynced(roundId, holeNumber) {
  const db = await getDb();
  const existing = await db.get('holeStats', [roundId, holeNumber]);
  if (existing) {
    existing._source = 'server';
    await db.put('holeStats', existing);
  }
}

/**
 * A write the server will never accept (see PERMANENT_STAT_REJECTIONS): keep the
 * player's answer on the device, but stop calling it unsynced.
 *
 * Needed because 'local' now MEANS "re-queue this" — `reconcileOrphanedStats`
 * revives any local row with nothing on the queue, and a permanently rejected
 * write has had its queue entry deliberately removed. Left as 'local' the two
 * would fight for ever: dropped on every drain, re-queued on every foreground.
 *
 * Not 'server', because the server does not have it and the strip must not
 * claim it does.
 */
export async function markHoleStatRejected(roundId, holeNumber) {
  const db = await getDb();
  const existing = await db.get('holeStats', [roundId, holeNumber]);
  if (existing) {
    existing._source = 'rejected';
    await db.put('holeStats', existing);
  }
}

// --- Leaderboards ---

export async function getLeaderboard(tournamentId, roundNumber) {
  const db = await getDb();
  return db.get('leaderboards', [tournamentId, roundNumber]);
}

export async function putLeaderboard(tournamentId, roundNumber, data) {
  const db = await getDb();
  return db.put('leaderboards', {
    ...data,
    tournamentId,
    roundNumber,
    _cachedAt: Date.now(),
  });
}

// --- Sync Queue ---

/**
 * WHO the queue is draining for.
 *
 * Writes outlive a sign-out: the queue is IndexedDB, not session state. It used
 * to drain whatever was there under whoever was signed in, so player A's
 * unsent scores went to the server with player B's token — refused if B could
 * not score that round, ACCEPTED (and attributed to B) if B could. Each entry
 * is now stamped with the uid that made it, and only the current owner's
 * entries are sent or counted; the rest wait on this phone for their owner.
 * Entries written before the stamp existed have no owner and drain as before.
 *
 * Set from `SyncProcessor` in App.js, so this module never imports Firebase.
 */
let syncOwner = null;
export function setSyncOwner(uid) { syncOwner = uid || null; }
const ownedByCurrentUser = (e) => !e.owner || e.owner === syncOwner;

export async function addToSyncQueue(entry) {
  const db = await getDb();
  // Deduplicate: if an existing pending/failed entry targets the same endpoint
  // with the same roundId + holeNumber (or teamRoundId + holeNumber, for
  // shared-ball team formats), replace it instead of appending.
  const dedupeKey = entry.roundId ? 'roundId' : entry.teamRoundId ? 'teamRoundId' : null;
  if (dedupeKey && entry.holeNumber) {
    const all = await db.getAll('syncQueue');
    const duplicate = all.find(
      (e) =>
        e.endpoint === entry.endpoint &&
        e[dedupeKey] === entry[dedupeKey] &&
        e.holeNumber === entry.holeNumber &&
        (e.status === 'pending' || e.status === 'failed')
    );
    if (duplicate) {
      duplicate.payload = entry.payload;
      duplicate.owner = syncOwner;
      duplicate.status = 'pending';
      duplicate.createdAt = Date.now();
      return db.put('syncQueue', duplicate);
    }
  }
  return db.add('syncQueue', {
    ...entry,
    owner: syncOwner,
    status: 'pending',
    retryCount: 0,
    createdAt: Date.now(),
  });
}

// How long a write waits after a failed attempt before the processor picks it
// up again. A phone in a tunnel must not spin against a dead network — and it
// must not give up either, which is what the old terminal 'failed' status did.
// The last value repeats for every attempt past the end of the list.
const RETRY_BACKOFF_MS = [5000, 15000, 60000, 5 * 60000, 15 * 60000, 60 * 60000];

/** Attempts before an entry stops being retried automatically and becomes a
 *  dead letter the player is told about. High on purpose: a stat is small, and
 *  a train journey can easily eat half a dozen tries. */
const MAX_SYNC_RETRIES = 12;

const backoffFor = (retryCount) =>
  RETRY_BACKOFF_MS[Math.min(retryCount, RETRY_BACKOFF_MS.length - 1)];

export async function getPendingSyncEntries() {
  const db = await getDb();
  const all = await db.getAllFromIndex('syncQueue', 'status', 'pending');
  const now = Date.now();
  // An entry that failed recently is still pending — it is just not due yet.
  // Skipping it here rather than dropping it is what makes a failure a delay
  // instead of a loss.
  return all
    .filter(ownedByCurrentUser)
    .filter((e) => !e.nextAttemptAt || e.nextAttemptAt <= now)
    .sort((a, b) => a.createdAt - b.createdAt);
}

export async function removeSyncEntry(id) {
  const db = await getDb();
  return db.delete('syncQueue', id);
}

/**
 * A rejected attempt, NOT a dead entry.
 *
 * This used to set `status = 'failed'` on the first bad response, and
 * `getPendingSyncEntries` only ever returns 'pending' — so a single 502 from a
 * restarting backend, or the HTML a captive portal answers with, retired the
 * write permanently. `retryFailedSyncEntries` was written to undo that and was
 * never called from anywhere, so nothing ever did.
 *
 * Now the entry stays pending behind a backoff and only becomes a dead letter
 * after MAX_SYNC_RETRIES, at which point it is surfaced rather than deleted.
 */
export async function markSyncEntryFailed(id) {
  const db = await getDb();
  const entry = await db.get('syncQueue', id);
  if (!entry) return;
  entry.retryCount = (entry.retryCount || 0) + 1;
  entry.lastError = Date.now();
  if (entry.retryCount >= MAX_SYNC_RETRIES) {
    entry.status = 'failed';
    entry.nextAttemptAt = null;
  } else {
    entry.status = 'pending';
    entry.nextAttemptAt = Date.now() + backoffFor(entry.retryCount);
  }
  await db.put('syncQueue', entry);
}

export async function getSyncQueueCount() {
  const db = await getDb();
  const pending = await db.getAllFromIndex('syncQueue', 'status', 'pending');
  return pending.filter(ownedByCurrentUser).length;
}

/** Everything of the current user's not yet on the server — what signing out
 *  would leave behind on this phone. */
export async function getUnsentCount() {
  const db = await getDb();
  const all = await db.getAll('syncQueue');
  return all.filter((e) => ownedByCurrentUser(e)
    && (e.status === 'pending' || e.status === 'failed')).length;
}

/**
 * Which holes of this round have a stat write still ON the queue — pending,
 * backing off, or dead-lettered.
 *
 * Exists so `useHoleStats` can spot a hole that is marked unsynced in the
 * `holeStats` store but has NOTHING queued to sync it. See
 * `reconcileOrphanedStats` — that gap is a silent, permanent loss of the
 * player's answer, and this is the only way to detect one.
 */
export async function getQueuedStatHoles(roundId) {
  const db = await getDb();
  const all = await db.getAll('syncQueue');
  return new Set(
    all
      .filter((e) => e.statHole
        && String(e.roundId) === String(roundId)
        && (e.status === 'pending' || e.status === 'failed'))
      .map((e) => Number(e.holeNumber)),
  );
}

// --- Players (cached per group) ---

export async function getPlayersForGroup(tournamentId, roundNumber, groupNumber) {
  const group = await getScoringGroupByKey(tournamentId, roundNumber, groupNumber);
  return group?.players || null;
}

async function getScoringGroupByKey(tournamentId, roundNumber, groupNumber) {
  const db = await getDb();
  return db.get('scoringGroups', [tournamentId, roundNumber, groupNumber]);
}

export async function putPlayersForGroup(tournamentId, roundNumber, groupNumber, players) {
  const db = await getDb();
  const existing = await db.get('scoringGroups', [tournamentId, roundNumber, groupNumber]);
  const entry = existing || { tournamentId, roundNumber, groupNumber };
  entry.players = players;
  entry._cachedAt = Date.now();
  return db.put('scoringGroups', entry);
}

// Returns the most-recently-cached tournament's scoring groups, so the offline
// home/group-picker can route to scoring without a live playerStatus lookup.
// Shape: { tournamentId, cachedAt, groups: [...] } or null when nothing cached.
export async function getMostRecentScoringGroups() {
  const db = await getDb();
  const all = await db.getAll('scoringGroups');
  if (!all.length) return null;

  // Find the tournament with the freshest cache entry.
  let bestTid = null;
  let bestAt = -1;
  for (const g of all) {
    const at = g._cachedAt || 0;
    if (at > bestAt) { bestAt = at; bestTid = g.tournamentId; }
  }
  if (bestTid == null) return null;

  const groups = all
    .filter((g) => g.tournamentId === bestTid)
    .sort((a, b) =>
      (a.roundNumber - b.roundNumber) || (a.groupNumber - b.groupNumber)
    );
  return { tournamentId: bestTid, cachedAt: bestAt, groups };
}

// --- Offline Sessions ---

export async function getOfflineSession() {
  const db = await getDb();
  return db.get('offlineSessions', 'active');
}

export async function putOfflineSession(session) {
  const db = await getDb();
  return db.put('offlineSessions', {
    ...session,
    id: 'active',
    _updatedAt: Date.now(),
    _createdAt: session._createdAt || Date.now(),
  });
}

export async function clearOfflineSession() {
  const db = await getDb();
  return db.delete('offlineSessions', 'active');
}

// --- Sync failed helpers ---

export async function getFailedSyncCount() {
  const db = await getDb();
  const all = await db.getAllFromIndex('syncQueue', 'status', 'failed');
  return all.filter(ownedByCurrentUser).length;
}

/**
 * Put every dead letter back in the queue, due immediately.
 *
 * It does NOT delete anything. The old version dropped entries past a retry
 * ceiling, which for a hole stat means the player's answer is gone with nothing
 * to show for it — and this is the function they reach through a "Retry" they
 * pressed on purpose, so the one thing it must not do is throw the work away.
 * An entry that keeps failing keeps its retryCount, so it is visibly a problem
 * rather than a silent one.
 */
export async function retryFailedSyncEntries({ reset = true } = {}) {
  const db = await getDb();
  const failed = (await db.getAllFromIndex('syncQueue', 'status', 'failed'))
    .filter(ownedByCurrentUser);
  const tx = db.transaction('syncQueue', 'readwrite');
  for (const entry of failed) {
    entry.status = 'pending';
    entry.nextAttemptAt = null;
    // `reset` is the difference between a player asking and the app guessing.
    // The automatic revival on session start KEEPS the count, so an entry that
    // is failing for a reason a new network will not fix gets one attempt per
    // session and then dead-letters again — rather than a fresh twelve every
    // time the app opens, for ever. Only the explicit "Retry" starts it over.
    if (reset) entry.retryCount = 0;
    await tx.store.put(entry);
  }
  await tx.done;
  if (failed.length) window.dispatchEvent(new CustomEvent('syncQueueChanged'));
}

/** Clear the backoff on everything pending, so a player who taps "Retry" gets
 *  an attempt now rather than in fifteen minutes. */
export async function dueNowAllPending() {
  const db = await getDb();
  const all = await db.getAllFromIndex('syncQueue', 'status', 'pending');
  const waiting = all.filter((e) => e.nextAttemptAt);
  if (!waiting.length) return;
  const tx = db.transaction('syncQueue', 'readwrite');
  for (const entry of waiting) {
    entry.nextAttemptAt = null;
    await tx.store.put(entry);
  }
  await tx.done;
}
